import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import type { User } from "oidc-client-ts";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { JSX } from "react";

import {
  completeSignin,
  isCallbackUrl,
  openPasswordReset,
  registerAccount,
  rememberUser,
  restoreUser,
  signinWithIdentityProvider,
  signinWithPassword,
  userManager,
  type PasswordSigninResult,
} from "./userManager.js";

interface AuthState {
  status: "loading" | "anonymous" | "authenticated";
  user: User | null;
  signIn: (loginHint?: string) => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<PasswordSigninResult>;
  signInWithIdentityProvider: (alias: string) => Promise<void>;
  resetPassword: () => Promise<void>;
  register: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");

  useEffect(() => {
    const manager = userManager();
    let active = true;
    let appUrlListener: PluginListenerHandle | null = null;
    let appStateListener: PluginListenerHandle | null = null;

    const apply = (next: User | null): void => {
      if (!active) {
        return;
      }
      // Erst hinterlegen, dann zeichnen: sonst fragt die erste Abfrage ohne Token.
      rememberUser(next);
      setUser(next);
      setStatus(next && !next.expired ? "authenticated" : "anonymous");
    };

    const finishSignin = async (url?: string): Promise<void> => {
      try {
        apply(await completeSignin(url));
      } catch {
        apply(null);
      }
    };

    const refresh = async (): Promise<void> => {
      apply(await restoreUser());
    };

    const start = async (): Promise<void> => {
      if (!Capacitor.isNativePlatform()) {
        if (isCallbackUrl()) {
          await finishSignin();
        } else {
          await refresh();
        }
        return;
      }

      appUrlListener = await CapacitorApp.addListener("appUrlOpen", ({ url }) => {
        if (isCallbackUrl(url)) {
          void finishSignin(url);
        }
      });
      appStateListener = await CapacitorApp.addListener("appStateChange", ({ isActive }) => {
        if (isActive) void refresh();
      });
      const launchUrl = await CapacitorApp.getLaunchUrl();
      if (launchUrl && isCallbackUrl(launchUrl.url)) {
        await finishSignin(launchUrl.url);
      } else {
        await refresh();
      }
    };

    void start().catch(() => apply(null));

    const forget = (): void => apply(null);
    const renewExpired = (): void => { void refresh(); };
    const refreshWhenVisible = (): void => {
      if (document.visibilityState === "visible") void refresh();
    };
    manager.events.addUserLoaded(apply);
    manager.events.addUserUnloaded(forget);
    manager.events.addAccessTokenExpired(renewExpired);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      active = false;
      manager.events.removeUserLoaded(apply);
      manager.events.removeUserUnloaded(forget);
      manager.events.removeAccessTokenExpired(renewExpired);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      void appUrlListener?.remove();
      void appStateListener?.remove();
    };
  }, []);

  const signIn = useCallback(async (loginHint?: string) => {
    await userManager().signinRedirect({
      ...(loginHint ? { extraQueryParams: { login_hint: loginHint } } : {}),
    });
  }, []);

  const signOut = useCallback(async () => {
    await userManager().signoutRedirect();
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      signIn,
      signInWithPassword: signinWithPassword,
      signInWithIdentityProvider: signinWithIdentityProvider,
      resetPassword: openPasswordReset,
      signOut,
      register: registerAccount,
    }),
    [status, user, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth benötigt einen AuthProvider.");
  }
  return context;
}
